import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Phone, Calendar, Mail, MessageSquare, CheckSquare, Plus, Trash2, ListChecks, Pencil, X, Check,
} from "lucide-react";
import { toast } from "sonner";

const TASK_TYPES = [
  { v: "call", label: "Call", icon: Phone, color: "text-blue-700 bg-blue-50 border-blue-200" },
  { v: "meeting", label: "Meeting", icon: Calendar, color: "text-violet-700 bg-violet-50 border-violet-200" },
  { v: "email", label: "Email", icon: Mail, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { v: "note", label: "Note", icon: MessageSquare, color: "text-slate-700 bg-slate-50 border-slate-200" },
  { v: "todo", label: "To-do", icon: CheckSquare, color: "text-amber-700 bg-amber-50 border-amber-200" },
];

const typeMeta = (v) => TASK_TYPES.find((t) => t.v === v) || TASK_TYPES[3];

export default function AccountTasks({ accountId }) {
  const [tasks, setTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState("call");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [dueAt, setDueAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  // Inline editor state — one task edited at a time
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const reload = async () => {
    const r = await api.get(`/accounts/${accountId}/tasks`);
    setTasks(r.data);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [accountId]);

  const reset = () => {
    setType("call"); setTitle(""); setNotes("");
    setOccurredAt(new Date().toISOString().slice(0, 16));
    setDueAt("");
  };

  const save = async () => {
    if (!title.trim() && !notes.trim()) {
      return toast.error("Add a title or notes");
    }
    setSaving(true);
    try {
      await api.post(`/accounts/${accountId}/tasks`, {
        type,
        title: title.trim() || null,
        notes: notes.trim() || null,
        occurred_at: new Date(occurredAt).toISOString(),
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        completed: false,
      });
      toast.success(`${typeMeta(type).label} logged`);
      reset();
      setShowForm(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (task) => {
    try {
      await api.patch(`/accounts/${accountId}/tasks/${task.id}`, { completed: !task.completed });
      setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, completed: !t.completed, completed_at: !t.completed ? new Date().toISOString() : null } : t));
    } catch (_) { toast.error("Failed to update"); }
  };

  const remove = async (task) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/accounts/${accountId}/tasks/${task.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (_) { toast.error("Failed to delete"); }
  };

  // ---- Inline edit ----
  const startEdit = (task) => {
    setEditingId(task.id);
    setEditDraft({
      type: task.type,
      title: task.title || "",
      notes: task.notes || "",
      // datetime-local expects `YYYY-MM-DDTHH:MM` — slice off the trailing timezone/seconds
      occurred_at: task.occurred_at ? new Date(task.occurred_at).toISOString().slice(0, 16) : "",
      due_at: task.due_at ? new Date(task.due_at).toISOString().slice(0, 16) : "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };
  const saveEdit = async () => {
    if (!editDraft) return;
    if (!editDraft.title.trim() && !editDraft.notes.trim()) {
      return toast.error("Title or notes required");
    }
    try {
      // Send nulls explicitly so blanking a field actually clears it server-side.
      const payload = {
        type: editDraft.type,
        title: editDraft.title.trim() || null,
        notes: editDraft.notes.trim() || null,
        occurred_at: editDraft.occurred_at ? new Date(editDraft.occurred_at).toISOString() : null,
        due_at: editDraft.due_at ? new Date(editDraft.due_at).toISOString() : null,
      };
      const r = await api.patch(`/accounts/${accountId}/tasks/${editingId}`, payload);
      setTasks((prev) => prev.map((t) => t.id === editingId ? { ...t, ...r.data } : t));
      toast.success("Task updated");
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const visible = tasks.filter((t) => {
    if (filter === "all") return true;
    if (filter === "open") return !t.completed;
    if (filter === "done") return t.completed;
    return t.type === filter;
  });

  return (
    <Card className="border-slate-200" data-testid="account-tasks">
      <CardHeader className="border-b border-slate-100 py-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-blue-600" />
          Tasks &amp; activity
          <Badge variant="outline" className="text-[10px] font-mono">{tasks.length}</Badge>
        </CardTitle>
        <Button
          data-testid="account-task-new"
          size="sm"
          onClick={() => setShowForm((s) => !s)}
          className="gap-1.5 bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-3.5 w-3.5" /> {showForm ? "Cancel" : "Log task"}
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {/* New task form */}
        {showForm && (
          <div className="border border-blue-200 bg-blue-50/40 rounded-md p-3 space-y-3" data-testid="account-task-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="account-task-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_TYPES.map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">When</Label>
                <Input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Title</Label>
              <Input
                data-testid="account-task-title"
                placeholder={type === "todo" ? "What needs to be done?" : `Brief summary of the ${typeMeta(type).label.toLowerCase()}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea
                data-testid="account-task-notes"
                rows={3}
                placeholder="Outcomes, action items, sentiment, follow-ups…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            {type === "todo" && (
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Due</Label>
                <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); reset(); }}>Cancel</Button>
              <Button
                data-testid="account-task-save"
                size="sm"
                onClick={save}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? "Saving…" : `Log ${typeMeta(type).label}`}
              </Button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {[
            { v: "all", label: `All (${tasks.length})` },
            { v: "open", label: `Open (${tasks.filter((t) => !t.completed).length})` },
            { v: "done", label: `Done (${tasks.filter((t) => t.completed).length})` },
            ...TASK_TYPES.map((t) => ({ v: t.v, label: t.label })),
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`account-task-filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider transition ${
                filter === f.v
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Task list */}
        <div className="space-y-1.5" data-testid="account-task-list">
          {visible.length === 0 ? (
            <div className="text-center py-6 text-xs text-slate-400">
              No tasks yet — log a call, meeting, email, note, or to-do to start the activity history.
            </div>
          ) : (
            visible.map((task) => {
              const m = typeMeta(task.type);
              const Icon = m.icon;
              return (
                <div
                  key={task.id}
                  data-testid={`account-task-${task.id}`}
                  className={`flex gap-3 p-3 border rounded-md group transition ${
                    task.completed
                      ? "bg-slate-50 border-slate-200 opacity-70"
                      : "bg-white border-slate-200 hover:border-blue-300"
                  }`}
                >
                  {task.type === "todo" && (
                    <Checkbox
                      data-testid={`account-task-toggle-${task.id}`}
                      checked={task.completed}
                      onCheckedChange={() => toggleComplete(task)}
                      className="mt-0.5"
                    />
                  )}
                  <div className={`h-7 w-7 flex-shrink-0 rounded-md border ${m.color} flex items-center justify-center`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className={`${m.color} text-[10px] py-0 px-1.5`}>{m.label}</Badge>
                        <span className="text-slate-500">
                          {new Date(task.occurred_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="text-slate-500">{task.created_by_name}</span>
                        {task.due_at && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-amber-700 font-mono text-[10px]">
                              Due {new Date(task.due_at).toLocaleDateString("en-GB")}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => (editingId === task.id ? cancelEdit() : startEdit(task))}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition"
                          title={editingId === task.id ? "Cancel edit" : "Edit"}
                          data-testid={`account-task-edit-${task.id}`}
                        >
                          {editingId === task.id ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          onClick={() => remove(task)}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {editingId === task.id && editDraft ? (
                      <div className="mt-2 space-y-2" data-testid={`account-task-editor-${task.id}`}>
                        <div className="grid grid-cols-2 gap-2">
                          <Select value={editDraft.type} onValueChange={(v) => setEditDraft((d) => ({ ...d, type: v }))}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TASK_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input
                            type="datetime-local"
                            value={editDraft.occurred_at}
                            onChange={(e) => setEditDraft((d) => ({ ...d, occurred_at: e.target.value }))}
                            className="h-8 text-xs"
                          />
                        </div>
                        <Input
                          placeholder="Title (optional)"
                          value={editDraft.title}
                          onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                          className="h-8 text-sm"
                          data-testid={`account-task-edit-title-${task.id}`}
                        />
                        <Textarea
                          placeholder="Notes (optional)"
                          value={editDraft.notes}
                          onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
                          rows={3}
                          className="text-sm"
                          data-testid={`account-task-edit-notes-${task.id}`}
                        />
                        <div className="flex items-center justify-between">
                          <Input
                            type="datetime-local"
                            value={editDraft.due_at}
                            onChange={(e) => setEditDraft((d) => ({ ...d, due_at: e.target.value }))}
                            placeholder="Due"
                            className="h-8 text-xs w-56"
                          />
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={cancelEdit}>Cancel</Button>
                            <Button size="sm" onClick={saveEdit} data-testid={`account-task-save-${task.id}`} className="bg-blue-600 hover:bg-blue-700 gap-1">
                              <Check className="h-3 w-3" /> Save
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {task.title && (
                          <div className={`mt-0.5 text-sm font-semibold ${task.completed ? "line-through text-slate-400" : "text-slate-900"}`}>
                            {task.title}
                          </div>
                        )}
                        {task.notes && (
                          <div className={`text-xs mt-0.5 whitespace-pre-wrap ${task.completed ? "text-slate-400" : "text-slate-600"}`}>
                            {task.notes}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
